// Short, well-established background notes. Arabic star-name origins follow the standard
// etymologies (Kunitzsch & Smart, "A Dictionary of Modern Star Names"); only names with a
// clear Arabic origin are listed.

export interface ArabicName {
  ar: string;
  tr: string;
  meaning: string; // empty when the Arabic is a proper name rather than a phrase to translate
  note?: string;
  /** false where the Western name is Greek or Latin: then this is the star's Arabic name, not the name's origin. */
  fromArabic?: false;
}

export const ARABIC_STAR_NAMES: Record<string, ArabicName> = {
  Aldebaran: { ar: 'الدَّبَرَان', tr: 'al-Dabarān', meaning: 'the follower', note: 'It follows the Pleiades across the sky.' },
  Betelgeuse: { ar: 'يَدُ الْجَوْزَاء', tr: 'Yad al-Jawzāʾ', meaning: 'the hand of al-Jawzāʾ', note: 'The European name comes from a medieval misreading of this Arabic phrase.' },
  Rigel: { ar: 'رِجْلُ الْجَوْزَاء', tr: 'Rijl al-Jawzāʾ', meaning: 'the foot of al-Jawzāʾ' },
  Altair: { ar: 'النَّسْرُ الطَّائِر', tr: 'al-Nasr al-Ṭāʾir', meaning: 'the flying eagle' },
  Vega: { ar: 'النَّسْرُ الْوَاقِع', tr: 'al-Nasr al-Wāqiʿ', meaning: 'the swooping eagle' },
  Deneb: { ar: 'ذَنَبُ الدَّجَاجَة', tr: 'Dhanab al-Dajājah', meaning: 'the tail of the hen' },
  Fomalhaut: { ar: 'فَمُ الْحُوت', tr: 'Fam al-Ḥūt', meaning: 'the mouth of the fish' },
  Algol: { ar: 'رَأْسُ الْغُول', tr: 'Raʾs al-Ghūl', meaning: 'the head of the ghoul', note: 'Its brightness dips every 2.87 days as a companion star passes in front of it.' },
  Achernar: { ar: 'آخِرُ النَّهْر', tr: 'Ākhir al-Nahr', meaning: 'the end of the river' },
  Alnilam: { ar: 'النِّظَام', tr: 'al-Nidhām', meaning: 'the string of pearls' },
  Alnitak: { ar: 'النِّطَاق', tr: 'al-Niṭāq', meaning: 'the girdle' },
  Mintaka: { ar: 'الْمِنْطَقَة', tr: 'al-Minṭaqah', meaning: 'the belt' },
  Saiph: { ar: 'سَيْفُ الْجَبَّار', tr: 'Sayf al-Jabbār', meaning: 'the sword of the giant' },
  Denebola: { ar: 'ذَنَبُ الْأَسَد', tr: 'Dhanab al-Asad', meaning: 'the tail of the lion' },
  Dubhe: { ar: 'الدُّبّ', tr: 'al-Dubb', meaning: 'the bear' },
  Merak: { ar: 'الْمَرَاقّ', tr: 'al-Marāqq', meaning: 'the flank (of the bear)' },
  Phecda: { ar: 'فَخِذُ الدُّبّ', tr: 'Fakhidh al-Dubb', meaning: 'the thigh of the bear' },
  Megrez: { ar: 'الْمَغْرِز', tr: 'al-Maghriz', meaning: 'the root of the tail' },
  Alkaid: { ar: 'الْقَائِد', tr: 'al-Qāʾid', meaning: 'the leader', note: 'Leader of banāt naʿsh, “the daughters of the bier”, the Arabic name for the Plough’s handle.' },
  Mizar: { ar: 'الْمِئْزَر', tr: 'al-Miʾzar', meaning: 'the waist-wrap' },
  Hamal: { ar: 'الْحَمَل', tr: 'al-Ḥamal', meaning: 'the lamb' },
  Mirfak: { ar: 'مِرْفَقُ الثُّرَيَّا', tr: 'Mirfaq al-Thurayyā', meaning: 'the elbow of the Pleiades' },
  Menkar: { ar: 'الْمَنْخِر', tr: 'al-Mankhir', meaning: 'the nostril (of the Whale)' },
  Alphard: { ar: 'الْفَرْد', tr: 'al-Fard', meaning: 'the solitary one', note: 'It shines alone in an otherwise faint part of the sky.' },
  Zubenelgenubi: { ar: 'الزُّبَانَى الْجَنُوبِيّ', tr: 'al-Zubāná al-Janūbī', meaning: 'the southern claw' },
  Zubeneschamali: { ar: 'الزُّبَانَى الشَّمَالِيّ', tr: 'al-Zubāná al-Shamālī', meaning: 'the northern claw' },
  Unukalhai: { ar: 'عُنُقُ الْحَيَّة', tr: 'ʿUnuq al-Ḥayyah', meaning: 'the neck of the snake' },
  Rasalhague: { ar: 'رَأْسُ الْحَوَّاء', tr: 'Raʾs al-Ḥawwāʾ', meaning: 'the head of the snake charmer' },
  Rasalgethi: { ar: 'رَأْسُ الْجَاثِي', tr: 'Raʾs al-Jāthī', meaning: 'the head of the kneeling one' },
  Thuban: { ar: 'الثُّعْبَان', tr: 'al-Thuʿbān', meaning: 'the serpent', note: 'It was once the pole star.' },
  Eltanin: { ar: 'التِّنِّين', tr: 'al-Tinnīn', meaning: 'the dragon' },
  Rastaban: { ar: 'رَأْسُ التِّنِّين', tr: 'Raʾs al-Tinnīn', meaning: 'the head of the dragon' },
  Sadr: { ar: 'الصَّدْر', tr: 'al-Ṣadr', meaning: 'the breast (of the hen)' },
  Schedar: { ar: 'الصَّدْر', tr: 'al-Ṣadr', meaning: 'the breast' },
  Caph: { ar: 'الْكَفّ', tr: 'al-Kaff', meaning: 'the palm (of the hand)' },
  Ruchbah: { ar: 'الرُّكْبَة', tr: 'al-Rukbah', meaning: 'the knee' },
  Shaula: { ar: 'الشَّوْلَة', tr: 'al-Shawlah', meaning: 'the raised tail (the sting)' },
  Lesath: { ar: 'اللَّسْعَة', tr: 'al-Lasʿah', meaning: 'the sting' },
  Acrab: { ar: 'الْعَقْرَب', tr: 'al-ʿAqrab', meaning: 'the scorpion' },
  Dschubba: { ar: 'الْجَبْهَة', tr: 'al-Jabhah', meaning: 'the forehead' },
  'Kaus Australis': { ar: 'الْقَوْس', tr: 'al-Qaws', meaning: 'the bow', note: '“Australis” is Latin for southern.' },
  Alpheratz: { ar: 'سُرَّةُ الْفَرَس', tr: 'Surrat al-Faras', meaning: 'the navel of the horse', note: 'Its other name, Sirrah, comes from this Arabic name.' },
  Markab: { ar: 'مَنْكِبُ الْفَرَس', tr: 'Mankib al-Faras', meaning: 'the shoulder of the horse', note: 'Originally the name of the next star along, β Pegasi.' },
  Scheat: { ar: 'السَّاق', tr: 'al-Sāq', meaning: 'the shin' },
  Enif: { ar: 'الْأَنْف', tr: 'al-Anf', meaning: 'the nose' },
  Alhena: { ar: 'الْهَنْعَة', tr: 'al-Hanʿah', meaning: 'the brand (a mark on a camel)' },
  Sadalsuud: { ar: 'سَعْدُ السُّعُود', tr: 'Saʿd al-Suʿūd', meaning: 'the good fortune of good fortunes' },
  Sadalmelik: { ar: 'سَعْدُ الْمَلِك', tr: 'Saʿd al-Malik', meaning: 'the good fortune of the king' },
  Sadachbia: { ar: 'سَعْدُ الْأَخْبِيَة', tr: 'Saʿd al-Akhbiyah', meaning: 'the good fortune of the tents' },
  'Deneb Algedi': { ar: 'ذَنَبُ الْجَدْي', tr: 'Dhanab al-Jady', meaning: 'the tail of the young goat' },
  Algedi: { ar: 'الْجَدْي', tr: 'al-Jady', meaning: 'the young goat' },
  Dabih: { ar: 'الذَّابِح', tr: 'al-Dhābiḥ', meaning: 'the slaughterer' },
  Albali: { ar: 'الْبَالِع', tr: 'al-Bāliʿ', meaning: 'the swallower' },
  Aludra: { ar: 'الْعُذْرَة', tr: 'al-ʿUdhrah', meaning: 'the maidenhood' },
  Adhara: { ar: 'الْعَذَارَى', tr: 'al-ʿAdhārá', meaning: 'the maidens' },
  Wezen: { ar: 'الْوَزْن', tr: 'al-Wazn', meaning: 'the weight' },
  Sirius: {
    ar: 'الشِّعْرَى', tr: 'al-Shiʿrá', meaning: '', fromArabic: false,
  },
  Procyon: { ar: 'الشِّعْرَى الْغُمَيْصَاء', tr: 'al-Shiʿrá al-Ghumayṣāʾ', meaning: 'the bleary-eyed Shiʿrá', fromArabic: false },
  Canopus: { ar: 'سُهَيْل', tr: 'Suhayl', meaning: '', fromArabic: false, note: 'Famous in Arabic poetry; its first appearance before dawn was said to mark the end of the summer heat.' },
  Antares: { ar: 'قَلْبُ الْعَقْرَب', tr: 'Qalb al-ʿAqrab', meaning: 'the heart of the scorpion', fromArabic: false, note: 'Its European name is Greek: “rival of Ares (Mars)”.' },
  Regulus: { ar: 'قَلْبُ الْأَسَد', tr: 'Qalb al-Asad', meaning: 'the heart of the lion', fromArabic: false, note: 'Its European name is Latin: “little king”.' },
  Spica: { ar: 'السِّمَاكُ الْأَعْزَل', tr: 'al-Simāk al-Aʿzal', meaning: 'the unarmed Simāk', fromArabic: false },
  Arcturus: { ar: 'السِّمَاكُ الرَّامِح', tr: 'al-Simāk al-Rāmiḥ', meaning: 'the Simāk with the lance', fromArabic: false },
  Capella: { ar: 'الْعَيُّوق', tr: 'al-ʿAyyūq', meaning: '', fromArabic: false, note: 'Its European name is Latin: “little she-goat”.' },
  Polaris: { ar: 'الْجَدْي', tr: 'al-Jady', meaning: 'the young goat', fromArabic: false, note: 'Arab navigators used it to find north.' },
  'Rigil Kentaurus': { ar: 'رِجْلُ قَنْطُورِس', tr: 'Rijl Qanṭūris', meaning: 'the foot of the Centaur', note: 'The nearest star system to the Sun, 4.37 light years away. These vowels are one reading of the medieval name; Arabic dictionaries give the constellation as Qinṭūrus.' },
  Alnair: { ar: 'النَّيِّر', tr: 'al-Nayyir', meaning: 'the bright one' },
  Diphda: { ar: 'الضِّفْدِع', tr: 'al-Ḍifdiʿ', meaning: 'the frog', note: 'In full al-ḍifdiʿ al-thānī, “the second frog”.' },
  Algieba: { ar: 'الْجَبْهَة', tr: 'al-Jabhah', meaning: 'the forehead (of the lion)' },
  Sheratan: { ar: 'الشَّرَطَان', tr: 'al-Sharaṭān', meaning: 'the two signs' },
  Almach: { ar: 'عَنَاقُ الْأَرْض', tr: 'ʿAnāq al-Arḍ', meaning: 'the caracal (desert lynx)' },
  Alrescha: { ar: 'الرِّشَاء', tr: 'al-Rishāʾ', meaning: 'the cord' },
  Menkalinan: { ar: 'مَنْكِبُ ذِي الْعِنَان', tr: 'Mankib Dhī al-ʿInān', meaning: 'the shoulder of the rein-holder' },
  Elnath: { ar: 'النَّطْح', tr: 'al-Naṭḥ', meaning: 'the butting (horn)' },
  Pherkad: { ar: 'الْفَرْقَد', tr: 'al-Farqad', meaning: 'the calf' },
  Alphecca: { ar: 'الْفَكَّة', tr: 'al-Fakkah', meaning: 'the broken (ring)' },
  Izar: { ar: 'الْإِزَار', tr: 'al-Izār', meaning: 'the loincloth' },
  Muphrid: { ar: 'الْمُفْرَد', tr: 'al-Mufrad', meaning: 'the solitary one', note: 'The Western spelling keeps an old misreading of the vowels.' },
  Algorab: { ar: 'الْغُرَاب', tr: 'al-Ghurāb', meaning: 'the crow' },
  Gienah: { ar: 'الْجَنَاح', tr: 'al-Janāḥ', meaning: 'the wing' },
  Alkes: { ar: 'الْكَأْس', tr: 'al-Kaʾs', meaning: 'the cup' },
  Sulafat: { ar: 'السُّلَحْفَاة', tr: 'al-Sulaḥfāh', meaning: 'the tortoise' },
  Kitalpha: { ar: 'قِطْعَةُ الْفَرَس', tr: 'Qiṭʿat al-Faras', meaning: 'part of the horse' },
  Errai: { ar: 'الرَّاعِي', tr: 'al-Rāʿī', meaning: 'the shepherd' },
  Adhafera: { ar: 'الضَّفِيرَة', tr: 'al-Ḍafīrah', meaning: 'the lock of hair', note: 'Originally the name of the star cluster in Coma Berenices.' },
  Zaniah: { ar: 'الزَّاوِيَة', tr: 'al-Zāwiyah', meaning: 'the corner' },
};

// BEGIN generated Arabic names (scratchpad arabic/apply.py; verified against the sources in docs)
/** Arabic names of the 88 modern constellations, fully vocalised (pausal form). */
export const CONSTELLATION_ARABIC: Record<string, { ar: string; tr: string }> = {
  And: { ar: 'الْمَرْأَةُ الْمُسَلْسَلَة', tr: 'al-Marʾah al-Musalsalah' },
  Ant: { ar: 'مُفْرِغَةُ الْهَوَاء', tr: 'Mufrighat al-Hawāʾ' },
  Aps: { ar: 'طَائِرُ الْفِرْدَوْس', tr: 'Ṭāʾir al-Firdaws' },
  Aqr: { ar: 'الدَّلْو', tr: 'al-Dalw' },
  Aql: { ar: 'الْعُقَاب', tr: 'al-ʿUqāb' },
  Ara: { ar: 'الْمِجْمَرَة', tr: 'al-Mijmarah' },
  Ari: { ar: 'الْحَمَل', tr: 'al-Ḥamal' },
  Aur: { ar: 'مُمْسِكُ الْأَعِنَّة', tr: 'Mumsik al-Aʿinnah' },
  Boo: { ar: 'الْعَوَّاء', tr: 'al-ʿAwwāʾ' },
  Cae: { ar: 'آلَةُ النَّقَّاش', tr: 'Ālat al-Naqqāsh' },
  Cam: { ar: 'الزَّرَافَة', tr: 'al-Zarāfah' },
  Cnc: { ar: 'السَّرَطَان', tr: 'al-Saraṭān' },
  CVn: { ar: 'السَّلُوقِيَّان', tr: 'al-Salūqiyyān' },
  CMa: { ar: 'الْكَلْبُ الْأَكْبَر', tr: 'al-Kalb al-Akbar' },
  CMi: { ar: 'الْكَلْبُ الْأَصْغَر', tr: 'al-Kalb al-Aṣghar' },
  Cap: { ar: 'الْجَدْي', tr: 'al-Jady' },
  Car: { ar: 'الْقَاعِدَة', tr: 'al-Qāʿidah' },
  Cas: { ar: 'ذَاتُ الْكُرْسِيّ', tr: 'Dhāt al-Kursī' },
  Cen: { ar: 'قِنْطُورُس', tr: 'Qinṭūrus' },
  Cep: { ar: 'الْمُلْتَهِب', tr: 'al-Multahib' },
  Cet: { ar: 'قَيْطُس', tr: 'Qayṭus' },
  Cha: { ar: 'الْحِرْبَاء', tr: 'al-Ḥirbāʾ' },
  Cir: { ar: 'الْبِيكَار', tr: 'al-Bīkār' },
  Col: { ar: 'الْحَمَامَة', tr: 'al-Ḥamāmah' },
  Com: { ar: 'الْهُلْبَة', tr: 'al-Hulbah' },
  CrA: { ar: 'الْإِكْلِيلُ الْجَنُوبِيّ', tr: 'al-Iklīl al-Janūbī' },
  CrB: { ar: 'الْإِكْلِيلُ الشَّمَالِيّ', tr: 'al-Iklīl al-Shamālī' },
  Crv: { ar: 'الْغُرَاب', tr: 'al-Ghurāb' },
  Crt: { ar: 'الْبَاطِيَة', tr: 'al-Bāṭiyah' },
  Cru: { ar: 'الصَّلِيبُ الْجَنُوبِيّ', tr: 'al-Ṣalīb al-Janūbī' },
  Cyg: { ar: 'الدَّجَاجَة', tr: 'al-Dajājah' },
  Del: { ar: 'الدُّلْفِين', tr: 'al-Dulfīn' },
  Dor: { ar: 'أَبُو سَيْف', tr: 'Abū Sayf' },
  Dra: { ar: 'التِّنِّين', tr: 'al-Tinnīn' },
  Equ: { ar: 'قِطْعَةُ الْفَرَس', tr: 'Qiṭʿat al-Faras' },
  Eri: { ar: 'النَّهْر', tr: 'al-Nahr' },
  For: { ar: 'الْكُور', tr: 'al-Kūr' },
  Gem: { ar: 'التَّوْأَمَان', tr: 'al-Tawʾamān' },
  Gru: { ar: 'الْكُرْكِيّ', tr: 'al-Kurkī' },
  Her: { ar: 'الْجَاثِي', tr: 'al-Jāthī' },
  Hor: { ar: 'السَّاعَة', tr: 'al-Sāʿah' },
  Hya: { ar: 'الشُّجَاع', tr: 'al-Shujāʿ' },
  Hyi: { ar: 'حَيَّةُ الْمَاء', tr: 'Ḥayyat al-Māʾ' },
  Ind: { ar: 'الْهِنْدِيّ', tr: 'al-Hindī' },
  Lac: { ar: 'الْعَظَاءَة', tr: 'al-ʿAdhāʾah' },
  Leo: { ar: 'الْأَسَد', tr: 'al-Asad' },
  LMi: { ar: 'الْأَسَدُ الْأَصْغَر', tr: 'al-Asad al-Aṣghar' },
  Lep: { ar: 'الْأَرْنَب', tr: 'al-Arnab' },
  Lib: { ar: 'الْمِيزَان', tr: 'al-Mīzān' },
  Lup: { ar: 'السَّبُع', tr: 'al-Sabuʿ' },
  Lyn: { ar: 'الْوَشَق', tr: 'al-Washaq' },
  Lyr: { ar: 'الْقِيثَارَة', tr: 'al-Qīthārah' },
  Men: { ar: 'الْجَبَل', tr: 'al-Jabal' },
  Mic: { ar: 'الْمِجْهَر', tr: 'al-Mijhar' },
  Mon: { ar: 'وَحِيدُ الْقَرْن', tr: 'Waḥīd al-Qarn' },
  Mus: { ar: 'الذُّبَابَة', tr: 'al-Dhubābah' },
  Nor: { ar: 'مُرَبَّعُ النَّجَّار', tr: 'Murabbaʿ al-Najjār' },
  Oct: { ar: 'الثُّمْن', tr: 'al-Thumn' },
  Oph: { ar: 'الْحَوَّاء', tr: 'al-Ḥawwāʾ' },
  Ori: { ar: 'الْجَبَّار', tr: 'al-Jabbār' },
  Pav: { ar: 'الطَّاوُوس', tr: 'al-Ṭāwūs' },
  Peg: { ar: 'الْفَرَسُ الْأَعْظَم', tr: 'al-Faras al-Aʿdham' },
  Per: { ar: 'حَامِلُ رَأْسِ الْغُول', tr: 'Ḥāmil Raʾs al-Ghūl' },
  Phe: { ar: 'الْعَنْقَاء', tr: 'al-ʿAnqāʾ' },
  Pic: { ar: 'آلَةُ الرَّسَّام', tr: 'Ālat al-Rassām' },
  Psc: { ar: 'الْحُوت', tr: 'al-Ḥūt' },
  PsA: { ar: 'الْحُوتُ الْجَنُوبِيّ', tr: 'al-Ḥūt al-Janūbī' },
  Pup: { ar: 'الْكَوْثَل', tr: 'al-Kawthal' },
  Pyx: { ar: 'بَيْتُ الْإِبْرَة', tr: 'Bayt al-Ibrah' },
  Ret: { ar: 'الشَّبَكَة', tr: 'al-Shabakah' },
  Sge: { ar: 'السَّهْم', tr: 'al-Sahm' },
  Sgr: { ar: 'الرَّامِي', tr: 'al-Rāmī' },
  Sco: { ar: 'الْعَقْرَب', tr: 'al-ʿAqrab' },
  Scl: { ar: 'مَعْمَلُ النَّحَّات', tr: 'Maʿmal al-Naḥḥāt' },
  Sct: { ar: 'التُّرْس', tr: 'al-Turs' },
  Ser: { ar: 'الْحَيَّة', tr: 'al-Ḥayyah' },
  Sex: { ar: 'السُّدْس', tr: 'al-Suds' },
  Tau: { ar: 'الثَّوْر', tr: 'al-Thawr' },
  Tel: { ar: 'الْمِرْقَب', tr: 'al-Mirqab' },
  Tri: { ar: 'الْمُثَلَّث', tr: 'al-Muthallath' },
  TrA: { ar: 'الْمُثَلَّثُ الْجَنُوبِيّ', tr: 'al-Muthallath al-Janūbī' },
  Tuc: { ar: 'الطُّوقَان', tr: 'al-Ṭūqān' },
  UMa: { ar: 'الدُّبُّ الْأَكْبَر', tr: 'al-Dubb al-Akbar' },
  UMi: { ar: 'الدُّبُّ الْأَصْغَر', tr: 'al-Dubb al-Aṣghar' },
  Vel: { ar: 'الشِّرَاع', tr: 'al-Shirāʿ' },
  Vir: { ar: 'الْعَذْرَاء', tr: 'al-ʿAdhrāʾ' },
  Vol: { ar: 'السَّمَكَةُ الطَّائِرَة', tr: 'al-Samakah al-Ṭāʾirah' },
  Vul: { ar: 'الثَّعْلَب', tr: 'al-Thaʿlab' },
};

// END generated Arabic names

/** Arabic without short vowels, tatweel or alif, yāʾ and tāʾ marbūṭa variants, for comparing names. */
export const plainArabic = (s: string) =>
  s.replace(/[\u064B-\u065F\u0670\u0640]/g, '').replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627').replace(/\u0649/g, '\u064A').replace(/\u0629/g, '\u0647').trim();

/**
 * Spoken English meanings where the IAU "English name" is just the Latin name again: plain
 * translations of the names only. Names of people from Greek legend (Andromeda, Perseus and others)
 * are left untranslated, as Ahillah does not retell myths.
 */
export const CONSTELLATION_MEANINGS: Record<string, string> = {
  Aqr: 'water carrier', Eri: 'river', Oph: 'serpent bearer',
  Peg: 'winged horse', UMa: 'Great Bear', UMi: 'Little Bear', Pup: 'stern of a ship',
  Car: 'keel of a ship', Vel: 'sails of a ship', Cap: 'sea goat', Lib: 'scales', Vir: 'maiden', Hya: 'water snake', Cru: 'Southern Cross',
  Ant: 'air pump', Aps: 'bird of paradise', Aql: 'eagle', Ara: 'altar', Ari: 'ram', Aur: 'charioteer', Boo: 'herdsman', Cae: 'engraver’s chisel',
  Cam: 'giraffe', Cnc: 'crab', CVn: 'hunting dogs', CMa: 'great dog', CMi: 'little dog', Cen: 'centaur', Cet: 'whale', Cha: 'chameleon',
  Cir: 'pair of compasses', Col: 'dove', Com: 'hair of Queen Berenice', CrA: 'southern crown', CrB: 'northern crown', Crv: 'crow', Crt: 'cup',
  Cyg: 'swan', Del: 'dolphin', Dor: 'dolphinfish', Dra: 'dragon', Equ: 'little horse', For: 'furnace', Gem: 'twins', Gru: 'crane',
  Hor: 'pendulum clock', Hyi: 'little water snake', Ind: 'Indian', Lac: 'lizard', Leo: 'lion', LMi: 'little lion', Lep: 'hare', Lup: 'wolf',
  Lyn: 'lynx', Lyr: 'lyre', Men: 'Table Mountain', Mic: 'microscope', Mon: 'unicorn', Mus: 'fly', Nor: 'carpenter’s square', Oct: 'octant',
  Pav: 'peacock', Phe: 'phoenix', Pic: 'painter’s easel', Psc: 'fishes', PsA: 'southern fish', Pyx: 'ship’s compass', Ret: 'net', Sge: 'arrow',
  Sgr: 'archer', Sco: 'scorpion', Scl: 'sculptor’s workshop', Sct: 'shield', Ser: 'serpent', Sex: 'sextant', Tau: 'bull', Tel: 'telescope',
  Tri: 'triangle', TrA: 'southern triangle', Tuc: 'toucan', Vol: 'flying fish', Vul: 'little fox',
};

/** What a constellation's name means in English, or '' when it is a person's name or already English. */
export const constellationMeaning = (c: { id: string; name: string; en: string }) =>
  CONSTELLATION_MEANINGS[c.id] ?? (c.en.toLowerCase() !== c.name.toLowerCase() && !CONSTELLATION_PEOPLE.has(c.id) ? c.en.toLowerCase() : '');
/** Constellations named after people from Greek legend: named, not described. */
const CONSTELLATION_PEOPLE = new Set(['And', 'Cas', 'Cep', 'Her', 'Per', 'Ori']);

export const SHOWER_PARENTS: Record<string, string> = {
  QUA: 'asteroid 2003 EH1', LYR: 'comet C/1861 G1 (Thatcher)', ETA: 'Halley’s Comet (1P/Halley)',
  PER: 'comet 109P/Swift–Tuttle', DRA: 'comet 21P/Giacobini–Zinner', ORI: 'Halley’s Comet (1P/Halley)',
  STA: 'Comet Encke (2P/Encke)', NTA: 'Comet Encke (2P/Encke)', LEO: 'comet 55P/Tempel–Tuttle',
  GEM: 'asteroid (3200) Phaethon', URS: 'comet 8P/Tuttle',
};

export const BODY_NOTES: Record<string, string> = {
  Sun: 'Never look at the Sun directly, and never through binoculars or a telescope without a proper solar filter.',
  Moon: 'The Moon always keeps the same face towards Earth; the slight rocking you can see over a month is called libration.',
  Mercury: 'The innermost planet. It never strays far from the Sun, so look low in twilight.',
  Venus: 'The brightest planet, often called the evening or morning star. Binoculars show its phase like a small Moon.',
  Mars: 'The Red Planet. Its brightness changes a lot as Earth overtakes it roughly every 26 months.',
  Jupiter: 'The largest planet. Steady binoculars show up to four of its moons as tiny stars in a line.',
  Saturn: 'Its rings are visible in a small telescope. They appear to tilt and even vanish edge-on over a 15-year cycle.',
  Uranus: 'Just at the edge of naked-eye visibility under a very dark sky; easy in binoculars if you know where to look.',
  Neptune: 'The most distant planet. It needs binoculars or a telescope.',
};
